/* Making the thing you just discovered you need, without losing the form.
 *
 * Halfway through an invoice you find the customer does not exist yet. The
 * choice ERPNext's desk gives you is to leave, create them, come back and
 * start again — and the invoice you had half-typed is gone. So this is a
 * dialog with the *mandatory* fields only: enough to bring the record into
 * existence, nothing more, and the Link field fills itself in with it.
 *
 * Mandatory-only is the whole point. A quick entry that shows all 108 fields
 * of a Customer is the full form in a smaller box.
 */
import { useEffect, useMemo, useState } from 'react'
import { Loading } from '@jackdesk/shell'
import type { ErpError } from './api'
import { useErp } from './context'
import { buildRegistry, renderWith } from './controls'
import { evaluateCondition } from './depends'
import type { Doc, DocMeta } from './types'

export function QuickEntry({ doctype, initialName, onDone, onCancel }: {
  doctype: string
  /** What they had typed into the Link box, used as the obvious first value. */
  initialName?: string
  onDone: (name: string) => void
  onCancel: () => void
}) {
  const erp = useErp()
  const [meta, setMeta] = useState<DocMeta | null>(null)
  const [values, setValues] = useState<Doc>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    erp.meta(doctype)
      .then((m) => {
        if (cancelled) return
        setMeta(m)
        const start: Doc = {}
        for (const f of m.fields) if (f.default && f.default !== 'Today') start[f.fieldname] = f.default
        // The first plain text field is almost always the name of the thing,
        // so what they typed goes there rather than being thrown away.
        const first = m.fields.find((f) => f.reqd && f.fieldtype === 'Data')
        if (first && initialName) start[first.fieldname] = initialName
        setValues(start)
      })
      .catch((e) => { if (!cancelled) setBanner((e as ErpError).message) })
    return () => { cancelled = true }
  }, [erp, doctype, initialName])

  const registry = useMemo(() => buildRegistry((child) => meta?.children?.[child]?.fields ?? []), [meta])

  const asked = useMemo(
    () => (meta?.fields ?? []).filter((f) =>
      !f.hidden && !f.read_only &&
      (f.reqd || evaluateCondition(f.mandatory_depends_on, values, false)) &&
      !['Section Break', 'Column Break', 'Tab Break', 'Heading', 'HTML', 'Button'].includes(f.fieldtype)),
    [meta, values])

  const save = async () => {
    const missing: Record<string, string> = {}
    for (const f of asked) {
      const v = values[f.fieldname]
      if (v === undefined || v === null || v === '') missing[f.fieldname] = `${f.label || f.fieldname} is needed.`
    }
    if (Object.keys(missing).length) { setErrors(missing); return }

    setSaving(true); setBanner(null); setErrors({})
    try {
      const made = await erp.create(doctype, { ...values, doctype })
      onDone(String(made.name))
    } catch (e) {
      const error = e as ErpError
      setBanner(error.message)
      if (Object.keys(error.fields).length) {
        setErrors(Object.fromEntries(Object.entries(error.fields).map(([k, v]) => [k, String(v)])))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="palette-wrap" role="dialog" aria-modal="true" aria-label={`New ${doctype}`}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="palette" style={{ width: 'min(520px, 94vw)' }}>
        <div className="row-between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
          <strong>New {meta?.label ?? doctype}</strong>
          <button className="btn btn-quiet btn-sm" onClick={onCancel} aria-label="Close">×</button>
        </div>

        <div style={{ padding: 16, display: 'grid', gap: 14, maxHeight: '60vh', overflowY: 'auto' }}>
          {!meta ? <Loading rows={3} label={`Loading ${doctype}`} />
            : asked.length === 0 ? (
              <p className="small muted" style={{ margin: 0 }}>
                A {meta.label ?? doctype} needs nothing filled in. Create it and edit it after.
              </p>
            ) : asked.map((f) => (
              <div key={f.fieldname} data-field={f.fieldname}>
                {renderWith(registry, {
                  field: f, value: values[f.fieldname], doc: values,
                  readOnly: false, required: true, error: errors[f.fieldname] || undefined,
                  onChange: (v) => {
                    setValues((c) => ({ ...c, [f.fieldname]: v }))
                    setErrors((e) => (e[f.fieldname] ? { ...e, [f.fieldname]: '' } : e))
                  },
                })}
              </div>
            ))}
          {banner ? <div className="err" role="alert">{banner}</div> : null}
        </div>

        <div className="row" style={{ padding: 12, borderTop: '1px solid var(--line)', gap: 8 }}>
          <span className="grow small muted">Only what it cannot exist without.</span>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}
